import { jsx } from "react/jsx-runtime";
import { ButtonGroup } from "@zillow/constellation-10";
import { resolveSpacing, v8ResponsiveToPanda } from "@zillow/zrs-cms";
import { generateBlockList } from "../../components/BlockList/BlockList.esm.js";
const ButtonGroupAny = ButtonGroup;
const DIRECTION_MAP = {
  row: {
    orientation: "horizontal"
  },
  "row-reverse": {
    orientation: "horizontal",
    reverse: true
  },
  column: {
    orientation: "vertical"
  },
  "column-reverse": {
    orientation: "vertical",
    reverse: true
  }
};
const BUTTON_TYPE_MAP = {
  primary: {
    tone: "brand",
    emphasis: "filled"
  },
  secondary: {
    tone: "brand",
    emphasis: "outlined"
  },
  tertiary: {
    tone: "neutral",
    emphasis: "outlined"
  },
  caution: {
    tone: "critical",
    emphasis: "filled"
  }
};
function resolveOrientation(direction) {
  const mapDir = (d) => DIRECTION_MAP[d] ?? DIRECTION_MAP.row;
  if (direction.lg_lte === void 0 && direction.sm_lte === void 0) {
    const base = mapDir(direction.default);
    return {
      orientation: base.orientation,
      reverse: base.reverse ?? false
    };
  }
  const smVal = direction.sm_lte;
  const lgVal = direction.lg_lte;
  const mapOrientation = (d) => mapDir(d).orientation;
  const mapReverse = (d) => mapDir(d).reverse ?? false;
  if (smVal !== void 0 && lgVal !== void 0) {
    return {
      orientation: {
        base: mapOrientation(smVal),
        md: mapOrientation(lgVal),
        xl: mapOrientation(direction.default)
      },
      reverse: {
        base: mapReverse(smVal),
        md: mapReverse(lgVal),
        xl: mapReverse(direction.default)
      }
    };
  }
  if (lgVal !== void 0) {
    return {
      orientation: {
        base: mapOrientation(lgVal),
        xl: mapOrientation(direction.default)
      },
      reverse: {
        base: mapReverse(lgVal),
        xl: mapReverse(direction.default)
      }
    };
  }
  if (smVal !== void 0) {
    return {
      orientation: {
        base: mapOrientation(smVal),
        md: mapOrientation(direction.default),
        xl: mapOrientation(direction.default)
      },
      reverse: {
        base: mapReverse(smVal),
        md: mapReverse(direction.default),
        xl: mapReverse(direction.default)
      }
    };
  }
  return {
    orientation: mapOrientation(direction.default),
    reverse: mapReverse(direction.default)
  };
}
function resolveLayoutProp(value) {
  if (!value) return void 0;
  if (typeof value === "string") return value;
  if (value.lg_lte === void 0 && value.sm_lte === void 0) {
    return value.default || void 0;
  }
  return v8ResponsiveToPanda(value);
}
const SPACING_PROP_NAMES = ["margin", "marginTop", "marginRight", "marginBottom", "marginLeft", "padding", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft"];
const BlockButtonGroup = ({
  attrs: {
    direction,
    orientation,
    reverse,
    fluid = false,
    conjoined = false,
    buttonType,
    margin,
    justifyContent,
    alignItems,
    ...attrs
  },
  index,
  innerBlocks
}) => {
  const orientationProps = (() => {
    if (orientation !== void 0) {
      const isV8Shape = (v) => v !== null && typeof v === "object" && "default" in v;
      if (isV8Shape(orientation)) {
        return resolveOrientation(orientation);
      }
      return {
        orientation,
        reverse
      };
    }
    if (direction !== void 0) {
      return resolveOrientation(direction);
    }
    return {
      orientation: "horizontal",
      reverse: false
    };
  })();
  const buttonTypeToneEmphasis = (() => {
    if (!buttonType) return {};
    return BUTTON_TYPE_MAP[buttonType] ?? BUTTON_TYPE_MAP.primary;
  })();
  const fluidProp = (() => {
    if (fluid !== null && typeof fluid === "object" && "default" in fluid) {
      const v8Fluid = fluid;
      if (v8Fluid.lg_lte === void 0 && v8Fluid.sm_lte === void 0) {
        return v8Fluid.default;
      }
      const hasSm = "sm_lte" in v8Fluid;
      const hasLg = "lg_lte" in v8Fluid;
      if (hasSm && hasLg) {
        return {
          base: v8Fluid.sm_lte,
          lg: v8Fluid.lg_lte,
          xl: v8Fluid.default
        };
      }
      if (hasLg) {
        return {
          base: v8Fluid.lg_lte,
          xl: v8Fluid.default
        };
      }
      return {
        base: v8Fluid.sm_lte,
        lg: v8Fluid.default,
        xl: v8Fluid.default
      };
    }
    return fluid;
  })();
  const cssParts = {};
  const resolvedJustifyContent = resolveLayoutProp(justifyContent);
  if (resolvedJustifyContent) {
    cssParts.justifyContent = resolvedJustifyContent;
  }
  const resolvedAlignItems = resolveLayoutProp(alignItems);
  if (resolvedAlignItems) {
    cssParts.alignItems = resolvedAlignItems;
  }
  cssParts.rowGap = "tighter";
  if (margin) {
    for (const [k, v] of Object.entries(margin)) {
      const migrated = resolveSpacing(v);
      if (migrated !== void 0) cssParts[k] = migrated;
    }
  }
  const storedCss = attrs.css;
  if (storedCss) {
    for (const [k, v] of Object.entries(storedCss)) {
      if (SPACING_PROP_NAMES.includes(k)) {
        const migrated = resolveSpacing(v);
        if (migrated !== void 0) cssParts[k] = migrated;
      } else {
        cssParts[k] = v;
      }
    }
  }
  const {
    css: _css,
    ...attrsWithoutCss
  } = attrs;
  const restAttrs = {};
  for (const [k, v] of Object.entries(attrsWithoutCss)) {
    if (SPACING_PROP_NAMES.includes(k)) {
      const migrated = resolveSpacing(v);
      if (migrated !== void 0) cssParts[k] = migrated;
    } else {
      restAttrs[k] = v;
    }
  }
  const spreadProps = {
    ...orientationProps,
    ...buttonTypeToneEmphasis,
    fluid: fluidProp,
    conjoined,
    key: index,
    ...restAttrs
  };
  if (Object.keys(cssParts).length > 0) {
    spreadProps.css = cssParts;
  }
  if (process.env.NODE_ENV !== "production" && !spreadProps["aria-label"] && !spreadProps["aria-labelledby"]) {
    console.warn("[ButtonGroup] Missing aria-label or aria-labelledby. Add one to the block attrs for accessibility.");
  }
  return /* @__PURE__ */ jsx(ButtonGroupAny, { ...spreadProps, children: generateBlockList({
    blocks: innerBlocks
  }) });
};
export {
  BlockButtonGroup as default
};
